// -----------------------------------------------------------------------------
// useUndoAttendance
//
// Drives the "Marked attended · Ns · Undo" banner that appears for 30 seconds
// after RecordAttended succeeds (gate-first mode only).
//
// Usage:
//   const { showBanner, secondsLeft, startUndo, undo, dismiss } = useUndoAttendance();
//   // After gate dismisses:
//   startUndo(leadTreatmentId);
//   // Render:
//   {showBanner && <UndoBanner secondsLeft={secondsLeft} onUndo={undo} onDismiss={dismiss} />}
// -----------------------------------------------------------------------------
import { useState, useEffect, useRef, useCallback } from 'react';
import { dispatch } from 'src/redux/store';
import { revertAttended } from 'src/redux/slices/lead/appointmentThunks';

const UNDO_DURATION_S = 30;

export default function useUndoAttendance() {
    const [showBanner, setShowBanner] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(UNDO_DURATION_S);
    const ltIdRef = useRef(null);        // leadTreatmentId — stored in ref to avoid stale closure
    const intervalRef = useRef(null);

    const clearTimer = useCallback(() => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    // Clears the banner and resets the counter.
    const dismiss = useCallback(() => {
        clearTimer();
        setShowBanner(false);
        setSecondsLeft(UNDO_DURATION_S);
    }, [clearTimer]);

    // Starts the 30s countdown. Call this right after RecordAttended succeeds.
    const startUndo = useCallback((leadTreatmentId) => {
        ltIdRef.current = leadTreatmentId;
        clearTimer();
        setSecondsLeft(UNDO_DURATION_S);
        setShowBanner(true);
        intervalRef.current = setInterval(() => {
            setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
    }, [clearTimer]);

    // Auto-dismiss when counter reaches 0.
    useEffect(() => {
        if (showBanner && secondsLeft === 0) {
            dismiss();
        }
    }, [showBanner, secondsLeft, dismiss]);

    // Cleanup on unmount.
    useEffect(() => () => clearTimer(), [clearTimer]);

    // Dispatches the revert thunk and immediately hides the banner.
    const undo = useCallback(() => {
        clearTimer();
        setShowBanner(false);
        dispatch(revertAttended(ltIdRef.current));
    }, [clearTimer]);

    return { showBanner, secondsLeft, startUndo, undo, dismiss };
}
